import { Handle, Position } from 'reactflow';
import styled, { createGlobalStyle, css } from 'styled-components';
import { Button } from '../Button';
import { CheckIcon, LoadingDIcon } from '../Icons';
import { ICON_CLASS } from '../Icons/tokens';
import { Input } from '../Input';
import { Stack } from '../Layout';
import { Markdown } from '../Markdown/Markdown';
import { MarkdownEditor } from '../MarkdownEditor/MarkdownEditor';
import { StyledDropdownHandle, StyledFixedToolbar, markdownStyles as markdownEditorStyles } from '../MarkdownEditor/styled';
import { tokens as markdownEditorTokens } from '../MarkdownEditor/tokens';
import { Hint } from '../Misc/Hint';
import { getInterFontWeightCSS, negateVariable, theme } from '../theme';
import { Text } from '../Typography/Text';
import { StyledLinkGemsButton } from './LinkGemsButton';
import { VALID_GEM_CONNECTION, INVALID_GEM_CONNECTION, ADD_NEW_PORT_HANDLE, LEFT_HANDLE_CLASS, REACT_FLOW_CLASS, REACT_FLOW_CONNECTION_LINE, RIGHT_HANDLE_CLASS, OUTER_NODE_WRAPPER_CLASS, SUBFLOW_NODE_WRAPPER_CLASS, tokens, REACT_FLOW_NODE_SUBFLOW, DRAGGING_SUBFLOW_CLASS, REACT_FLOW_NODE } from './tokens';
import { NodeStateTypes } from './types';
const showAddNewPortHandleStyles = css`
  .${REACT_FLOW_CLASS}:has(.${REACT_FLOW_CONNECTION_LINE}) &:hover .${ADD_NEW_PORT_HANDLE} {
    display: flex !important;
    align-items: center;
    justify-content: center;
  }
`;
const buttonResetStyles = css`
  background: transparent;
  border: none;
  &:focus-visible {
    outline: none;
  }
`;
const placeCenterStyles = css`
  display: flex;
  justify-content: center;
  align-items: center;
`;
export const StyledGemLabel = styled.div`
  max-width: 180px;
  ${getInterFontWeightCSS(theme.fontWeight.medium)};
`;
export const StyledGemType = styled.div`
  color: ${({ status })=>tokens.Status[status].color};
  font-size: ${theme.fontSizes.x12};
  max-width: 180px;
`;
export const StyledStatus = styled(Stack)`
  position: absolute;
  top: ${theme.spaces.x12};
  right: ${theme.spaces.x12};
  width: ${theme.spaces.x16};
`;
export const TileWrapper = styled.div`
  height: ${({ height })=>height ? height + 'px' : tokens.Tile.height};
  width: ${tokens.Tile.width};
  position: relative;
  padding: ${theme.spaces.x12};
  display: flex;
  place-items: center;

  background: ${tokens.Node.background};
  border-radius: ${tokens.Node.borderRadius};
  &:hover {
    box-shadow: ${tokens.Node.hover.shadow};
  }

  &&& > .${LEFT_HANDLE_CLASS} {
    background: ${({ status, lineageHighlight })=>lineageHighlight === 'modified' ? tokens.Node.state.lineageModified.borderColor : lineageHighlight === 'unmodified' ? tokens.Node.state.lineageUnmodified.borderColor : tokens.Status[status].borderColor};
    left: -11px;
  }
  &&& > .${RIGHT_HANDLE_CLASS} {
    border-left-color: ${({ status, lineageHighlight })=>lineageHighlight === 'modified' ? tokens.Node.state.lineageModified.borderColor : lineageHighlight === 'unmodified' ? tokens.Node.state.lineageUnmodified.borderColor : tokens.Status[status].borderColor};
    right: -11px;
  }

  ${({ status, lineageHighlight })=>lineageHighlight === 'modified' ? `border: ${tokens.Node.state.lineageModified.borderWidth} solid ${tokens.Node.state.lineageModified.borderColor};` : lineageHighlight === 'unmodified' ? `border: ${tokens.Node.state.lineageUnmodified.borderWidth} solid ${tokens.Node.state.lineageUnmodified.borderColor};` : `border: 1px solid ${tokens.Status[status].borderColor};`};
  ${({ state, lineageHighlight })=>!lineageHighlight && (state === NodeStateTypes.error || state === NodeStateTypes.warning) ? `border-color: ${tokens.Node.state[state].borderColor};` : ''};

  ${({ selected })=>selected ? `
      box-shadow: ${tokens.Node.state.selected.shadow};
      border-width: 3px;
      transition: ${tokens.Tile.transition};
  ` : ''}
  > svg {
    width: 100%;
    height: 100%;
  }

  ${({ disabled })=>disabled && `
    filter: grayscale(1);
  `}
`;
export const OuterNodeWrapper = styled.div.attrs({
    className: OUTER_NODE_WRAPPER_CLASS
})`
  position: absolute;
  width: calc(100% + 50px);
  height: calc(100% + 60px);
  transform: translate(-50%, -50%);
  top: 50%;
  left: 50%;
  border: none;
  background-color: ${theme.colors.gray100};
  border-radius: 9px;
  opacity: ${({ $isHovered })=>$isHovered ? 0.5 : 0};
  z-index: -99;
  pointer-events: all;
`;
export const StyledGemNode = styled.div`
  ${showAddNewPortHandleStyles}
  position: relative;

  &.${VALID_GEM_CONNECTION} {
    cursor: crosshair !important;
  }

  &.${INVALID_GEM_CONNECTION} {
    cursor: no-drop !important;
  }
`;
export const NodeWrapper = styled.div`
  height: ${({ size })=>size ? size + 'px' : tokens.Node.height};
  width: ${({ size })=>size ? size + 'px' : tokens.Node.width};
  position: relative;
  display: flex;
  place-items: center;
  z-index: ${theme.zLayer.s};
  ${({ virtual, isGemBroken })=>(virtual || isGemBroken) && `
    filter: grayscale(1);
    opacity: .6;
  `}

  background: ${tokens.Node.background};
  border-radius: ${tokens.Node.borderRadius};

  outline-width: ${({ selected, modified, isDragging, lineageHighlight })=>lineageHighlight ? lineageHighlight === 'modified' ? tokens.Node.state.lineageModified.borderWidth : tokens.Node.state.lineageUnmodified.borderWidth : selected || isDragging ? tokens.Node.state.selected.borderWidth : modified ? tokens.Node.state.modified.borderWidth : tokens.Node.borderWidth};
  outline-color: ${({ selected, state, modified, isDragging, lineageHighlight })=>lineageHighlight ? lineageHighlight === 'modified' ? tokens.Node.state.lineageModified.borderColor : tokens.Node.state.lineageUnmodified.borderColor : modified === 'created' ? tokens.Node.state.created.borderColor : modified === 'updated' ? tokens.Node.state.modified.borderColor : state === NodeStateTypes.error || state === NodeStateTypes.warning ? tokens.Node.state[state].borderColor : selected || isDragging ? tokens.Node.state.selected.borderColor : tokens.Node.borderColor};
  outline-style: ${({ isDragging })=>isDragging ? tokens.Node.state.dragging.borderStyle : tokens.Node.borderStyle};
  outline-offset: ${({ selected })=>selected ? negateVariable(theme.spaces.x2) : `0`};

  &:hover {
    box-shadow: ${tokens.Node.hover.shadow};
  }

  .${LEFT_HANDLE_CLASS} {
    left: ${({ selected, isDragging })=>selected || isDragging ? '-12px' : '-11px'};
  }
  .${RIGHT_HANDLE_CLASS} {
    right: ${({ selected, isDragging })=>selected || isDragging ? '-12px' : '-11px'};
  }

  ${({ status, lineageHighlight })=>!lineageHighlight && status ? css`
          border-color: ${tokens.Status[status].borderColor};
          && > .${LEFT_HANDLE_CLASS} {
            background: ${tokens.Status[status].borderColor};
          }
          && > .${RIGHT_HANDLE_CLASS} {
            border-left-color: ${tokens.Status[status].borderColor};
          }
        ` : ``}
  > svg, img {
    width: 100%;
    height: 100%;
  }
`;
export const NodeLabel = styled(Stack)`
  position: absolute;
  ${({ fullWidth })=>fullWidth ? '' : `max-width: calc(100% + ${theme.spaces.x16});`}
  transform: translateX(-50%);
  bottom: calc(100% + ${theme.spaces.x6});
  left: 50%;
`;
export const NodeDescriptionHint = styled(Hint)`
  max-width: 400px;
`;
export const StyledNodeTypeLabel = styled(Text)`
  position: absolute;
  transform: translateX(-50%);
  top: calc(100% + ${theme.spaces.x6});
  left: 50%;
  max-width: calc(100% + ${theme.spaces.x20});
  display: flex;
  gap: ${theme.spaces.x4};
  place-items: center;
  white-space: nowrap;
`;
export const SuccessIcon = styled(CheckIcon)`
  &&& {
    color: ${theme.colors.success500};
  }
`;
export const LoadingIcon = styled(LoadingDIcon)`
  &&& {
    color: ${theme.colors.gray700};
  }
`;
export const StyledInterimContainer = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  border-radius: 0 ${theme.radius.x8};
  background: ${theme.colors.brand600};
  width: 21px;
  height: 21px;
  text-align: center;
  svg {
    color: ${theme.colors.white};
    width: 9.5px;
  }
`;
export const VerticalSeparator = styled.div`
  height: 20px;
  margin: ${theme.spaces.x4} 0;
  border-left: 1px solid ${theme.colors.gray200};
`;
export const HorizontalSeparator = styled.div`
  margin: 0 ${theme.spaces.x4};
  border-top: 1px solid ${theme.colors.gray200};
`;
export const NodeContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  padding: ${tokens.Node.NodeContainer.padding};
  gap: ${tokens.Node.NodeContainer.gap};
  border-radius: ${tokens.Node.NodeContainer.borderRadius};
  overflow: hidden;
`;
export const OpenButton = styled.button`
  ${buttonResetStyles}
  ${placeCenterStyles}
  flex-grow: 1;
  cursor: pointer;
  background: ${theme.colors.brand50};
  color: ${theme.colors.gray600};
  border-radius: 9px 9px 0 0;

  && .${ICON_CLASS} {
    color: ${theme.colors.gray500};
  }

  &:hover {
    background: ${theme.colors.brand600};
    color: ${theme.colors.white};

    && .${ICON_CLASS} {
      color: ${theme.colors.white};
    }
  }
`;
export const StyledInput = styled(Input)`
  min-width: 132px;
  svg {
    color: ${theme.colors.gray400};
  }
`;
export const ToolSection = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: calc(50% - ${tokens.Node.NodeContainer.padding});
  gap: 3px;

  && .${ICON_CLASS} {
    font-size: ${theme.fontSizes.x16};
  }
`;
export const ToolIconButton = styled(Button)`
  flex: 1;
  height: 100%;
  border: none;
  border-radius: ${({ $leftTool, $rightTool })=>$leftTool ? '0 0 0 9px' : $rightTool ? '0 0 9px 0' : '0 0 9px 9px'};
  && {
    background-color: ${theme.colors.brand50};
    color: ${theme.colors.gray600};
  }
  && .${ICON_CLASS} {
    color: ${theme.colors.gray500};
  }

  &[data-state='open'],
  &:hover {
    background-color: ${({ disabled })=>disabled ? theme.colors.brand50 : theme.colors.brand600};
    color: ${({ disabled })=>disabled ? theme.colors.gray600 : theme.colors.white};
    && .${ICON_CLASS} {
      color: ${({ disabled })=>disabled ? theme.colors.gray500 : theme.colors.white};
    }
  }
`;
export const Phase = styled(Text)`
  position: absolute;
  left: ${theme.spaces.x8};
  bottom: ${theme.spaces.x4};
  color: ${theme.colors.gray500};
`;
export const disableUserSelectClass = 'disable-user-select-for-graph-selection';
export const disableUserSelect = css`
  .${disableUserSelectClass} & {
    user-select: none;
  }
`;
export const SubMenuItem = styled.span`
  padding-left: ${theme.spaces.x4};
  font-size: ${theme.fontSizes.x12};
`;
export const PortLabel = styled(Text)`
  position: absolute;
  ${({ position })=>position === Position.Left ? `right: ${theme.spaces.x10};` : `left: ${theme.spaces.x6};`}
  top: 50%;
  transform: translateY(-50%);
`;
export const NodeTooltipWrapper = styled.p`
  word-wrap: break-word;
`;
export const StyledMark = styled.mark`
  border-radius: ${theme.spaces.x4};
  background-color: ${theme.colors.yellow300};
  padding: ${theme.spaces.x2} 0;
`;
export const AddNewPortHandleContainer = styled.div`
  position: relative;
  &::after {
    display: block;
    content: '';
    width: ${theme.spaces.x48};
    height: calc(${(props)=>`${props.nodeSize}px`} + ${theme.spaces.x40});
    bottom: ${negateVariable(theme.spaces.x20)};
    position: absolute;
    transform: translate(-100%);
    z-index: ${theme.zLayer.s};
  }
  ${showAddNewPortHandleStyles}
`;
export const AddNewPortHandle = styled(Handle)`
  display: none !important;
  z-index: ${theme.zLayer.m};
`;
const commentMarkdownStyles = css`
  border: none;
  box-shadow: none;
  min-height: 100%;
  min-width: 100%;
  position: relative;

  h1,
  h2 {
    background: ${theme.backgroundColor.brandPrimary};
    padding: ${theme.spaces.x8} ${theme.spaces.x12};

    [data-slate-placeholder='true'] {
      top: ${theme.spaces.x8} !important;
    }
  }

  h2 {
    background: ${theme.backgroundColor.primaryHover};
  }
`;
export const StyledMarkdownEditor = styled(MarkdownEditor)`
  ${commentMarkdownStyles}

  .${markdownEditorTokens.classNames.slateEditor} {
    margin: ${theme.spaces.x8} ${theme.spaces.x12};
    padding: 0;
  }
`;
const markdownPreviewStyles = css`
  padding: ${theme.spaces.x8} ${theme.spaces.x12};
  border: 2px solid transparent;
`;
export const StyledMarkdown = styled(Markdown)`
  && {
    ${markdownEditorStyles}
    ${commentMarkdownStyles}
    ${markdownPreviewStyles}
    
    em {
      background: transparent;
      padding: 0;
      margin: 0;
    }

    overflow-wrap: break-word;
  }
`;
export const StyledPlaceholder = styled.div`
  ${markdownPreviewStyles}
  color: ${theme.colors.gray500};
  opacity: 0.5;
`;
export const CommentNodeContainer = styled(StyledGemNode)`
  display: flex;
  background: ${theme.colors.white};
  visibility: ${({ hidden })=>hidden ? 'hidden' : 'visible'};
`;
export const CommentNodeWrapper = styled.div`
  background: ${theme.backgroundColor.primary};
  border: ${({ $selected: selected, $isDragging: isDragging })=>selected || isDragging ? `${tokens.Node.state.modified.borderWidth} solid ${tokens.Node.state.selected.borderColor}` : 'none'};
  border-radius: ${theme.radius.x2};
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  width: 100%;

  .${LEFT_HANDLE_CLASS} {
    left: ${({ $selected: selected, $isDragging: isDragging })=>selected || isDragging ? `${negateVariable(theme.spaces.x12)}` : '-11px'};
  }
  .${RIGHT_HANDLE_CLASS} {
    right: ${({ $selected: selected, $isDragging: isDragging })=>selected || isDragging ? `${negateVariable(theme.spaces.x12)}` : '-11px'};
  }

  &:before,
  &:after {
    background: ${theme.backgroundColor.primary};
    border: ${tokens.Node.state.modified.borderWidth} solid ${tokens.Node.state.selected.borderColor};
    border-radius: ${theme.radius.x10};
    content: '';
    display: ${({ $selected: selected })=>selected ? 'block' : 'none'};
    height: ${theme.sizes.x22};
    left: 0;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: ${theme.sizes.x8};
  }

  &:after {
    left: auto;
    right: 0;
    transform: translate(50%, -50%);
  }

  * {
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  ${StyledFixedToolbar} {
    background: ${theme.backgroundColor.primary};
    border: ${theme.radius.x2} solid ${theme.borderColor.tertiary};
    border-radius: ${theme.radius.x8};
    box-shadow: ${theme.shadows.lg};
    display: none;
    left: 50%;
    position: absolute;
    top: ${negateVariable(theme.sizes.x56)};
    transform: translateX(-50%);
  }

  &:focus-within {
    ${StyledFixedToolbar} {
      display: flex;
    }
  }

  ${StyledFixedToolbar}:has(${StyledDropdownHandle}[data-state='open']),
  ${StyledFixedToolbar}:has(${StyledLinkGemsButton}[data-state='open']) {
    display: flex;
  }

  ${StyledMarkdownEditor} {
    cursor: ${({ $selected: selected })=>selected ? 'text' : 'grab'};
  }
`;
export const LineageExpressionBadge = styled(Stack)`
  position: absolute;
  bottom: calc(100% + ${theme.spaces.x32});
  left: 50%;
  transform: translateX(-50%);
  background: ${theme.colors.gray100};
  border-radius: ${theme.radius.x6};
  border: 1px solid ${theme.colors.gray600};
  padding: ${theme.spaces.x8};
  box-shadow: none;
  max-width: 280px;
  z-index: ${theme.zLayer.m};
`;
export const LineageExpressionItem = styled(Stack)`
  width: 100%;
`;
export const LineageColumnName = styled(Stack)`
  border-radius: ${theme.radius.x4};
  padding: 0 ${theme.spaces.x4};
`;
export const LineageColumnNameText = styled(Text)`
  font-family: Inter, sans-serif;
  font-weight: ${theme.fontWeight.medium};
  font-size: ${theme.fontSizes.x12};
  line-height: ${theme.lineHeight.x16};
  letter-spacing: 0%;
  vertical-align: middle;
  color: ${theme.colors.gray700};
`;
export const LineageColumnIcon = styled(Stack)`
  align-items: center;
  color: ${theme.colors.gray500};
`;
export const SubFlowGlobalStyles = createGlobalStyle`
  .${REACT_FLOW_NODE_SUBFLOW} {
    pointer-events: none !important;

    .nodrag {
      > * {
        pointer-events: all;
      }
    }
  }

  .${REACT_FLOW_NODE_SUBFLOW}, .${REACT_FLOW_NODE} {
    &.${DRAGGING_SUBFLOW_CLASS}, &.dragging {
      z-index: ${tokens.Node.dragging.zIndex} !important;
    }
  }

  .${SUBFLOW_NODE_WRAPPER_CLASS} {
    cursor: default;
    position: relative;
    z-index: 1;

    > * {
      pointer-events: all;
    }
  }
`;
